import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import React, { FC } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SPACING, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
/* Store Custom Code - End */

interface OwnProps {
    extendedStyles?: ProductQuantityBreakPricingStyles;
}

export interface ProductQuantityBreakPricingStyles {
    viewLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    breakQtyHeader?: DataTableHeaderProps;
    breakPriceHeader?: DataTableHeaderProps;
    savingsHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    breakQtyCell?: DataTableCellBaseProps;
    breakPriceCell?: DataTableCellBaseProps;
    savingsCell?: DataTableCellBaseProps;
    // Store Custom Code
    breakpriceWrapper?: InjectableCss;
    lineWrapper?: InjectableCss;
    breakPriceSmallText?: TypographyPresentationProps;
    breakPriceBiggerText?: TypographyPresentationProps;
    breakPriceMiddleText?: TypographyPresentationProps;
    breakPriceRightText?: TypographyPresentationProps;
}

/* Store Custom Code - Start */
const headingStyles: DataTableHeaderProps = {
    alignX: "center",
    typographyProps: {
        size: TYPOGRAPHY.size.tiny,
    },
    css: css`
        height: 35px;
        background: none;
        padding: 6px;
    `,
};

const breakPriceTextStyle = {
    weight: TYPOGRAPHY.weight.medium,
    lineHeight: 1,
    css: css`
        color: ${getColor("text.dark")};
    `,
};
/* Store Custom Code - End */

export const productQuantityBreakPricingStyles: ProductQuantityBreakPricingStyles = {
    modal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: ${SPACING.standard}; /* Store Custom Code */
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    /* Store Custom Code - Start */
    breakQtyHeader: {
        ...headingStyles,
    },
    breakPriceHeader: {
        ...headingStyles,
    },
    savingsHeader: {
        ...headingStyles,
    },
    /* Store Custom Code - End */
    breakQtyCell: { alignX: "center" },
    breakPriceCell: { alignX: "center" },
    savingsCell: { alignX: "center" },
    // Store Custom Code
    viewLink: {
        typographyProps: {
            css: css`
                font-weight: ${TYPOGRAPHY.weight.bold};

                &:hover,
                &:focus {
                    text-decoration: underline;
                }
            `,
        },
    },
    breakpriceWrapper: {
        css: css`
            width: 100%;
        `,
    },
    lineWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 5px;

            &:last-child {
                padding-bottom: 0;
            }
        `,
    },
    breakPriceSmallText: {
        ...breakPriceTextStyle,
        size: TYPOGRAPHY.size.base,
    },
    breakPriceBiggerText: {
        ...breakPriceTextStyle,
        size: TYPOGRAPHY.size.large,
    },
    breakPriceMiddleText: {
        ...breakPriceTextStyle,
        css: css`
            margin-left: 5px;
            padding-right: 5px;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        {
                            values: [`${TYPOGRAPHY.size.veryTiny}`, null, `${TYPOGRAPHY.size.tiny}`, null, null],
                            properties: ["font-size"],
                        },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    breakPriceRightText: {
        ...breakPriceTextStyle,
        size: TYPOGRAPHY.size.large,
        css: css`
            margin-left: auto;
        `,
    },
};

const ProductQuantityBreakPricing: FC<OwnProps & HasProduct> = ({
    product,
    productInfo: { pricing },
    extendedStyles,
}) => {
    const showPricing = useAppSelector(state => state.context.showPricing);
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewLinkClickHandler = () => {
        setModalIsOpen(true);
    };

    const [styles] = React.useState(() => mergeToNew(productQuantityBreakPricingStyles, extendedStyles));

    if (
        !product ||
        product.quoteRequired ||
        !pricing ||
        !pricing.unitRegularBreakPrices ||
        pricing.unitRegularBreakPrices.length < 2 ||
        !showPricing
    ) {
        return null;
    }

    /* Store Custom Code - Start */

    const unitBreakPriceItem = pricing.unitRegularBreakPrices[0];

    return (
        <StyledWrapper {...styles.breakpriceWrapper}>
            {unitBreakPriceItem && (
                <StyledWrapper {...styles.lineWrapper}>
                    <Typography {...styles.breakPriceSmallText}>{translate("Your unit price:")}</Typography>
                    <Typography {...styles.breakPriceBiggerText}>{unitBreakPriceItem.breakPriceDisplay}</Typography>
                </StyledWrapper>
            )}
            {pricing!.unitRegularBreakPrices!.slice(1).map(breakPrice => (
                <StyledWrapper
                    key={`${breakPrice.breakQty}-${Math.floor(Math.random() * 10000)}`}
                    {...styles.lineWrapper}
                >
                    <Typography {...styles.breakPriceSmallText}>
                        {translate("Your Bulk Price")} ({breakPrice.breakQty}+) :
                    </Typography>
                    <Typography {...styles.breakPriceRightText}>{breakPrice.breakPriceDisplay}</Typography>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
    /* Store Custom Code - End */
};

export default withProduct(ProductQuantityBreakPricing);
